import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updatePassword, deleteUser } from 'firebase/auth';
import { auth } from '../firebase';
import { Plus, Users } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    role: 'user',
    permissions: {
      inventory: false,
      sales: false,
      reports: false,
      settings: false
    }
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), {
          username: formData.username,
          role: formData.role,
          permissions: formData.permissions
        });
      } else {
        // For Google users, create Firestore document only (no Firebase Auth user)
        if (formData.email.includes('@gmail.com') || formData.email.includes('@googlemail.com')) {
          // Create placeholder for Google user
          const userId = formData.email.replace(/[.@]/g, '_');
          await setDoc(doc(db, 'users', userId), {
            email: formData.email,
            username: formData.username,
            role: formData.role,
            permissions: formData.permissions,
            isGoogleUser: true,
            createdAt: new Date()
          });
        } else {
          // Create email/password user
          const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: formData.email,
            username: formData.username,
            role: formData.role,
            permissions: formData.permissions,
            createdAt: new Date()
          });
        }
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error: ' + error.message);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        // Note: This only deletes the Firestore document
        // To delete from Firebase Auth, you need Firebase Admin SDK
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      username: '',
      role: 'user',
      permissions: {
        inventory: false,
        sales: false,
        reports: false,
        settings: false
      }
    });
    setShowForm(false);
    setEditingUser(null);
  };

  const editUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      username: user.username,
      role: user.role,
      permissions: user.permissions
    });
    setShowForm(true);
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '20px',
      padding: '30px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{ color: '#333', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Users size={24} style={{ color: '#667eea' }} />
          User Management
        </h2>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          <Plus size={16} style={{ marginRight: '8px' }} />
          Add User
        </button>
      </div>

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ color: '#333', marginBottom: '20px' }}>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: '500' }}>Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={editingUser}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: '500' }}>Password</label>
                <input
                  type="password"
                  placeholder={editingUser ? "New Password (leave blank to keep current)" : "Password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                  disabled={editingUser && editingUser.email && editingUser.email.includes('gmail.com')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: '500' }}>Username</label>
                <input
                  type="text"
                  placeholder="Username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#333', fontWeight: '500' }}>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e1e5e9',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="permissions">
                <h4 style={{ color: '#333', marginBottom: '15px' }}>Permissions</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px'
                }}>
                  {Object.keys(formData.permissions).map(permission => (
                    <label key={permission} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions[permission]}
                        onChange={(e) => setFormData({
                          ...formData,
                          permissions: {
                            ...formData.permissions,
                            [permission]: e.target.checked
                          }
                        })}
                      />
                      <span style={{ textTransform: 'capitalize', fontSize: '14px' }}>{permission}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'} User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '15px' }}>
        {users.map(user => (
          <div key={user.id} style={{
            border: '2px solid #e1e5e9',
            borderRadius: '16px',
            padding: '20px',
            background: 'white'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '15px'
            }}>
              <div>
                <h4 style={{ color: '#333', margin: '0 0 5px 0', fontSize: '1.1rem' }}>{user.username}</h4>
                <p style={{ color: '#666', margin: '0 0 5px 0', fontSize: '0.9rem' }}>{user.email}</p>
                <span style={{
                  background: user.role === 'admin' ? '#667eea' : '#6c757d',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  textTransform: 'capitalize'
                }}>{user.role}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => editUser(user)} style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}>Edit</button>
                <button onClick={() => deleteUser(user.id)} style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}>Delete</button>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '8px'
            }}>
              {Object.entries(user.permissions || {}).map(([permission, hasAccess]) => (
                <div key={permission} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  background: hasAccess ? '#e8f5e8' : '#f8f9fa',
                  borderRadius: '6px',
                  border: `1px solid ${hasAccess ? '#4caf50' : '#e1e5e9'}`,
                  fontSize: '0.8rem'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: hasAccess ? '#4caf50' : '#ccc'
                  }} />
                  <span style={{
                    color: hasAccess ? '#2e7d32' : '#666',
                    textTransform: 'capitalize'
                  }}>
                    {permission}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserManagement;